import { cellAtWorld, cellCenter, heroWalkable, touchGrid } from '../grid.ts';
import { iatan2, idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, SendaNpc } from '../types.ts';
import type { Pt, TrailStone } from '../caza-types.ts';
import type { DenHowler, DenRules, DenState } from '../vertebras-types.ts';
import { damageEnemy, damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';

/**
 * The pack's den (docs/sendas/barbaro.md, chapter 2, act 3, the test: «La guarida de la manada»; the ground in
 * battle/vertebras-layout.ts). The last vertebra, wide, its cave low to the north with real boars' bones at the mouth, a
 * red dusk. Bera and Terrón launch the boat at the south slip (a bar of three minutes; a blow on Bera stops it a
 * moment): the boat in the water and the hero in it wins it; Bera down loses it. The whole pack comes out of the cave,
 * and one in three goes straight for the boat. The howlers keep their places either side of the mouth, far from the
 * boat: cutting a howl means leaving Bera, or a stone rolled along the cave's foot. Two howlers (as the ledge's, battle/howler.ts): they rise by turns and bristle
 * three seconds, then howl two and a half; struck for `cutBp` of their life in the howl, or a loose stone rolled on
 * them, the howl is cut; let out, the fallen get up and the pack heals a quarter. If one lets out its howl while the
 * other rears or howls, the howl is double: both at once, the pack healed half, twice the fallen up. The puzzle's rule in
 * small: the cracked slabs of the floor bear two of the hero's arrivals; at the third the slab creaks and sinks, taking
 * whatever stands on it (the hero falls, hurt; wolves are gone). Four loose stones on the rim roll in toward the middle.
 *
 * Cost: the pack is the act's list (a few dozen at most), looked at once a tick; the slabs six of three cells.
 */

const npc = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id && n.hp > 0);

const fresh = (s: BattleState, r: DenRules): DenState => {
  const howlers: DenHowler[] = r.firsts.map((t, k) => {
    const p = r.perches[k] as Pt;
    const e = spawnEnemy(s, 'wolf', 1, false, 0, p.x, p.y, r.howler.hp);
    return { id: e.id, next: t, phase: 0, until: 0, hpAt: 0, cd: 0, away: 0, bites: 0 };
  });
  return {
    work: 0,
    launched: -1,
    next: r.first,
    crow: r.crows.first,
    pack: [],
    fallen: [],
    howlers,
    cuts: 0,
    howls: 0,
    doubles: 0,
    slabs: r.slabs.map(() => ({ cracks: 0, creak: -1, sunk: false })),
    on: -1,
    stones: r.stones.map((p) => ({ mode: 0, x: p.x, y: p.y, left: 0, struck: [] })),
    job: -1,
    held: 0,
    sunk: 0,
    falls: 0,
    rushed: false,
    done: -1,
  };
};

/** Where the hero stands to push a stone: behind it, against its roll. */
export const denStand = (r: DenRules, k: number): Pt => {
  const p = r.stones[k] as Pt;
  const d = r.rolls[k] as Pt;
  return { x: p.x - idiv(d.x * 700, 1000), y: p.y - idiv(d.y * 700, 1000) };
};

/** The slab a cell is in (−1: none). */
export const denSlabOf = (r: DenRules, cell: number): number => r.slabs.findIndex((sl) => sl.cells.includes(cell));

/** A howler of the den by id (its state), if it is one. */
export const denHowlerOf = (s: BattleState, id: number): DenHowler | undefined => s.senda?.vert?.den?.howlers.find((x) => x.id === id);

/** A tap on a mark: 0 the boat (the hero goes to it), 1 + k the k-th loose stone (the hero goes behind it to push). */
export const denOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.vert?.den;
  const w = s.senda?.vert?.den;
  if (!r || !w) return;
  if (at === 0) {
    w.job = -1;
    orderMove(s, h, r.boat.x, r.boat.y);
    h.target = 0;
    h.forced = false;
    return;
  }
  const st = w.stones[at - 1];
  if (!st || st.mode !== 0) return;
  w.job = at;
  w.held = 0;
  const q = denStand(r, at - 1);
  orderMove(s, h, q.x, q.y);
  h.target = 0;
  h.forced = false;
};

/** A howl cut (struck enough, or a stone on it): it reels, and its next comes later. */
const cut = (s: BattleState, r: DenRules, w: DenState, hw: DenHowler, e: Enemy, cue: (c: string) => void): void => {
  hw.phase = 0;
  hw.next = s.waveT + r.howler.every;
  w.cuts++;
  e.stun = imax(e.stun, r.howler.stun);
  ev(s, { t: 'senda', kind: 'cutHowl', npc: '', x: e.x, y: e.y, n: w.cuts });
  cue(`cut${w.cuts}`);
};

/** A howl let out (double if the other is rearing or howling): the fallen up where they fell, the pack healed. */
const howled = (s: BattleState, r: DenRules, w: DenState, hw: DenHowler, e: Enemy, cue: (c: string) => void): void => {
  const H = r.howler;
  hw.phase = 0;
  hw.next = s.waveT + H.every;
  const other = w.howlers.find((o) => o !== hw && (o.phase === 1 || o.phase === 2));
  const double = !!other;
  if (other) {
    other.phase = 0;
    other.next = s.waveT + H.every;
    w.doubles++;
  }
  w.howls++;
  ev(s, { t: 'senda', kind: double ? 'vDouble' : 'howled', npc: '', x: e.x, y: e.y, n: w.howls });
  cue(double ? 'double' : `howled${w.howls}`);
  for (let k = 0; k < H.raise * (double ? 2 : 1) && w.fallen.length; k++) {
    const p = w.fallen.pop() as Pt;
    const up = spawnEnemy(s, 'wolf', 1, false, 0, p.x, p.y, r.wolfHp);
    w.pack.push(up.id);
    ev(s, { t: 'senda', kind: 'rise', npc: '', x: p.x, y: p.y });
  }
  w.fallen.length = 0;
  const heal = double ? r.doubleBp : H.healBp;
  for (const id of [...w.howlers.map((x) => x.id), ...w.pack]) {
    const o = s.enemies.find((x) => x.id === id && x.hp > 0);
    if (o) o.hp = imin(o.maxHp, o.hp + mulBp(o.maxHp, heal));
  }
};

const howlersTick = (s: BattleState, r: DenRules, w: DenState, cue: (c: string) => void): void => {
  const H = r.howler;
  for (const hw of w.howlers) {
    const e = s.enemies.find((x) => x.id === hw.id);
    if (!e || e.hp <= 0) {
      if (hw.phase !== 3) {
        hw.phase = 3;
        cue(w.howlers.every((x) => x.phase === 3) ? 'howlersDown' : 'howlerDown');
      }
      continue;
    }
    if (hw.cd > 0) hw.cd--;
    if (hw.phase === 0 && s.waveT >= hw.next && e.stun === 0) {
      hw.phase = 1;
      hw.until = s.tick + H.rear;
      ev(s, { t: 'senda', kind: 'rear', npc: '', x: e.x, y: e.y, n: H.rear });
      cue('rear1');
      continue;
    }
    if (hw.phase === 1 && s.tick >= hw.until) {
      hw.phase = 2;
      hw.until = s.tick + H.howl;
      hw.hpAt = e.hp;
      ev(s, { t: 'senda', kind: 'howl', npc: '', x: e.x, y: e.y, n: H.howl });
      continue;
    }
    if (hw.phase === 2) {
      if (hw.hpAt - e.hp >= mulBp(e.maxHp, H.cutBp)) cut(s, r, w, hw, e, cue);
      else if (s.tick >= hw.until) howled(s, r, w, hw, e, cue);
    }
  }
};

/** The slabs: the hero's arrivals counted; the third creaks and sinks with what stands on it. */
const slabsTick = (s: BattleState, r: DenRules, w: DenState, h: Hero | undefined, cue: (c: string) => void): void => {
  if (h) {
    const k = denSlabOf(r, cellAtWorld(s, h.x, h.y));
    if (k !== w.on) {
      w.on = k;
      const sl = w.slabs[k];
      if (sl && !sl.sunk && sl.creak < 0) {
        sl.cracks++;
        const at = (r.slabs[k] as { at: Pt }).at;
        ev(s, { t: 'senda', kind: 'vSlabCrack', npc: '', x: at.x, y: at.y, n: k * 10 + imin(3, sl.cracks) });
        if (sl.cracks >= 3) {
          sl.creak = s.tick + r.creak;
          ev(s, { t: 'senda', kind: 'vSlabCreak', npc: '', x: at.x, y: at.y, n: k });
        }
      }
    }
  }
  w.slabs.forEach((sl, k) => {
    if (sl.sunk || sl.creak < 0 || s.tick < sl.creak) return;
    sl.sunk = true;
    w.sunk++;
    const R = r.slabs[k] as { cells: number[]; at: Pt };
    const plugs = new Set(s.plugs ?? []);
    for (const c of R.cells) plugs.add(c);
    s.plugs = [...plugs].sort((a, b) => a - b);
    touchGrid(s);
    let took = 0;
    for (const e of s.enemies) {
      if (e.hp <= 0 || !R.cells.includes(cellAtWorld(s, e.x, e.y))) continue;
      damageEnemy(s, e, e.hp + e.maxHp, { pid: h?.pid ?? '', ignoresArmor: true });
      took++;
    }
    ev(s, { t: 'senda', kind: 'vSlabSink', npc: '', x: R.at.x, y: R.at.y, n: k * 100 + imin(99, took) });
    cue(`sunk${w.sunk}`);
    if (h && R.cells.includes(cellAtWorld(s, h.x, h.y))) {
      // Into the water under it, and up onto the floor beside.
      damageHero(s, h, mulBp(h.stats.maxHp, r.fallBp), null, 'physical', true);
      ev(s, { t: 'senda', kind: 'vFall', npc: '', x: h.x, y: h.y, n: 0 });
      w.falls++;
      let best = -1;
      let bd = 0x7fffffff;
      for (let dq = -2; dq <= 2; dq++)
        for (let dr = -2; dr <= 2; dr++) {
          const c = cellAtWorld(s, R.at.x + dq * 1000 + dr * 500, R.at.y + dr * 866);
          if (c < 0 || !heroWalkable(s, c)) continue;
          const p = cellCenter(s.map.radius, c);
          const d = (p.x - h.x) * (p.x - h.x) + (p.y - h.y) * (p.y - h.y);
          if (d < bd) {
            bd = d;
            best = c;
          }
        }
      if (best >= 0) {
        const p = cellCenter(s.map.radius, best);
        h.x = h.destX = h.ax = p.x;
        h.y = h.destY = h.ay = p.y;
      }
      h.path = [];
      h.moving = false;
      h.stun = imax(h.stun, 12);
      w.on = -1;
    }
  });
};

/** The loose stones: the one sent to, pushed after a moment behind it; those rolling strike what they meet. */
const stonesTick = (s: BattleState, r: DenRules, w: DenState, h: Hero | undefined, cue: (c: string) => void): void => {
  const R = r.stone;
  if (w.job > 0 && h) {
    const st = w.stones[w.job - 1] as TrailStone;
    const q = denStand(r, w.job - 1);
    if (st.mode !== 0) w.job = -1;
    else if (withinRange(h.x, h.y, q.x, q.y, R.reach) && !h.moving) {
      if (++w.held >= R.stand) {
        st.mode = 1;
        st.left = R.roll;
        ev(s, { t: 'senda', kind: 'stonePush', npc: '', x: st.x, y: st.y, n: w.job - 1 });
        w.job = -1;
        w.held = 0;
      }
    } else w.held = 0;
  }
  w.stones.forEach((st, k) => {
    if (st.mode !== 1) return;
    const d = r.rolls[k] as Pt;
    const step = imin(R.speed, st.left);
    st.x += idiv(d.x * step, 1000);
    st.y += idiv(d.y * step, 1000);
    st.left -= step;
    for (const e of s.enemies) {
      if (e.hp <= 0 || st.struck.includes(e.id) || !withinRange(e.x, e.y, st.x, st.y, R.width)) continue;
      st.struck.push(e.id);
      const hw = w.howlers.find((x) => x.id === e.id);
      const howling = !!hw && (hw.phase === 1 || hw.phase === 2);
      damageEnemy(s, e, R.hit, { pid: h?.pid ?? '', ignoresArmor: true });
      ev(s, { t: 'senda', kind: 'stoneHit', npc: '', x: e.x, y: e.y, n: howling ? 1 : 0 });
      if (howling && hw && e.hp > 0) {
        cut(s, r, w, hw, e, cue);
        cue('stone1');
      } else if (e.hp > 0) e.stun = imax(e.stun, R.stun);
    }
    if (st.left <= 0) st.mode = 2;
  });
};

export const denTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.vert?.den;
  const st = s.senda;
  if (!r || !st) return;
  const vs = (st.vert ??= {});
  const w = (vs.den ??= fresh(s, r));
  if (!s.started || w.done >= 0) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  // The pack fallen (for the howl), the pack gone.
  let k = 0;
  for (const id of w.pack) {
    const e = s.enemies.find((x) => x.id === id);
    if (!e || e.hp <= 0) {
      if (e && heroWalkable(s, cellAtWorld(s, e.x, e.y))) w.fallen.push({ x: e.x, y: e.y });
      if (w.fallen.length > 8) w.fallen.shift();
      continue;
    }
    w.pack[k++] = id;
  }
  w.pack.length = k;
  // Bera at the boat: her work goes on while no blow stops her (the people's rule leaves her flinch to the act).
  const b = npc(s, 'bera');
  if (b && w.launched < 0) {
    if (b.flinch > 0) b.flinch--;
    else if (++w.work >= r.launch) {
      w.launched = s.tick;
      ev(s, { t: 'senda', kind: 'vLaunched', npc: 'bera', x: r.boat.x, y: r.boat.y });
      cue('launched');
    }
  }
  for (const id of ['bera', 'terron', 'sten']) {
    const n = npc(s, id);
    if (n) n.facing = iatan2(r.mouth.y - n.y, r.mouth.x - n.x);
  }
  howlersTick(s, r, w, cue);
  slabsTick(s, r, w, h, cue);
  stonesTick(s, r, w, h, cue);
  // The pack out of the cave; crows off the sea.
  if (s.waveT >= w.next) {
    w.next = s.waveT + rngRange(s.rng, r.every[0], r.every[1]);
    const n = rngRange(s.rng, r.band[0], r.band[1]);
    for (let i = 0; i < n; i++) {
      const e = spawnEnemy(s, 'wolf', 1, false, 0, r.mouth.x + (i - (n >> 1)) * 450, r.mouth.y + (i % 2) * 350, r.wolfHp);
      w.pack.push(e.id);
    }
  }
  // The last rush: the boat nearly in, the whole pack out of the cave at once.
  if (!w.rushed && w.launched < 0 && w.work >= r.launch - r.rush.left) {
    w.rushed = true;
    for (let i = 0; i < r.rush.n; i++) {
      const e = spawnEnemy(s, 'wolf', 1, false, 0, r.mouth.x + ((i % 4) - 2) * 500, r.mouth.y + (i >> 2) * 450, r.wolfHp);
      w.pack.push(e.id);
    }
    ev(s, { t: 'senda', kind: 'vRush', npc: '', x: r.mouth.x, y: r.mouth.y, n: r.rush.n });
    cue('rush');
  }
  if (h && s.waveT >= w.crow) {
    w.crow = s.waveT + r.crows.every;
    for (let n = 0; n < r.crows.n; n++) spawnEnemy(s, 'crow', 1, false, 0, 9000 - n * 500, -2000 + n * 900, imax(1, r.wolfHp >> 1));
  }
  // In the boat once it is in the water.
  if (h && w.launched >= 0 && withinRange(h.x, h.y, r.boat.x, r.boat.y, r.board)) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'vBoard', npc: '', x: r.boat.x, y: r.boat.y });
  }
};

/**
 * The howlers move and bite by their own rule (rearing and howling they stand); every other wolf of the pack goes for
 * the boat when the hero isn't on it (true: the act moved it).
 */
export const denHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = s.rules?.senda?.vert?.den;
  const w = s.senda?.vert?.den;
  if (!r || !w || e.stun > 0) return false;
  const hw = w.howlers.find((x) => x.id === e.id);
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!hw) {
    // One wolf in three makes for Bera at the boat, unless the hero is on it.
    const b = npc(s, 'bera');
    if (!b || e.kind !== 'wolf' || e.id % 3 !== 0 || w.launched >= 0) return false;
    if (h && withinRange(h.x, h.y, e.x, e.y, 1400)) return false;
    if (withinRange(e.x, e.y, b.x, b.y, 800)) {
      if (b.x !== e.x || b.y !== e.y) e.facing = iatan2(b.y - e.y, b.x - e.x);
      if (e.cd === 0) {
        e.cd = e.reload;
        e.anim = 'attack';
        e.animT = 0;
      }
      return true;
    }
    const ox = e.x;
    const oy = e.y;
    stepToward(e, b.x, b.y, px(e));
    if (!heroWalkable(s, cellAtWorld(s, e.x, e.y))) {
      e.x = ox;
      e.y = oy;
      return false;
    }
    e.anim = 'walk';
    return true;
  }
  const H = r.howler;
  if (hw.phase === 1 || hw.phase === 2) {
    e.anim = hw.phase === 1 ? 'cast' : 'attack';
    if (h && (h.x !== e.x || h.y !== e.y)) e.facing = iatan2(h.y - e.y, h.x - e.x);
    return true;
  }
  // It keeps its place by the mouth: after the hero only while the hero is near it, and back again after its bites.
  const home = r.perches[w.howlers.indexOf(hw)] as Pt;
  const near = !!h && withinRange(h.x, h.y, home.x, home.y, r.leash);
  if (!h || !near || s.tick < hw.away) {
    if (withinRange(e.x, e.y, home.x, home.y, 250)) {
      e.anim = 'idle';
      if (h && (h.x !== e.x || h.y !== e.y)) e.facing = iatan2(h.y - e.y, h.x - e.x);
    } else {
      e.anim = 'walk';
      stepToward(e, home.x, home.y, px(e));
    }
    return true;
  }
  if (!withinRange(e.x, e.y, h.x, h.y, H.reach)) {
    e.anim = 'walk';
    const ox = e.x;
    const oy = e.y;
    stepToward(e, h.x, h.y, px(e));
    if (!heroWalkable(s, cellAtWorld(s, e.x, e.y))) {
      e.x = ox;
      e.y = oy;
      return false;
    }
    return true;
  }
  if (h.x !== e.x || h.y !== e.y) e.facing = iatan2(h.y - e.y, h.x - e.x);
  if (hw.cd === 0) {
    hw.cd = H.reload;
    e.anim = 'attack';
    e.animT = 0;
    damageHero(s, h, mulBp(h.stats.maxHp, H.biteBp), e, 'physical', false);
    hw.bites++;
    if (hw.bites % H.bites === 0) hw.away = s.tick + H.back;
  } else if (e.anim !== 'attack' || e.animT > 6) e.anim = 'idle';
  return true;
};

/** Bera's work at the boat, of all of it; then the boat in the water (the hero to it). */
export const denGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.vert?.den;
  const w = s.senda?.vert?.den;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: imin(r.launch, w.work), need: r.launch };
};

export const denResult = (s: BattleState): 'won' | null => {
  const w = s.senda?.vert?.den;
  return w && w.done >= 0 && s.tick >= w.done + 12 ? 'won' : null;
};
